import React from "react";
import { Context } from "@hotwired/stimulus";
import { ReactWrapperController } from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import { Card, CardHeader, CardTitle, CardContent, CardAction } from "@root/server/client/esweb/components/ui/card.tsx";
import { Badge } from "@root/server/client/esweb/components/ui/badge.tsx";
import { Button } from "@root/server/client/esweb/components/ui/button.tsx";
import { 
    Clock, 
    Activity, 
    Sparkles, 
    Eye, 
    Heart, 
    Mail, 
    Bell, 
    Webhook, 
    Search,
    ChevronRight,
    ExternalLink
} from "lucide-react";
import { monitor_row_name } from "./hook_filter_form.shared.ts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export const name = monitor_row_name;

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <MonitorRow {...props} />);
    }
}

interface MonitorRowProps {
    monitor: any; // HookFilterModel with aiModel
    editUrl: string;
    checksUrl: string;
}

function MonitorRow({ monitor, editUrl, checksUrl }: MonitorRowProps) {
    const lastCheck = monitor.last_check_at ? dayjs(monitor.last_check_at).fromNow() : "Never";
    
    return (
        <Card className="group hover:border-slate-300 transition-all duration-200 shadow-none py-0 gap-0">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 p-6 pb-3">
                <div className="space-y-1 min-w-0 flex-1">
                    <CardTitle className="text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                        <a href={editUrl} className="block truncate">{monitor.name}</a>
                    </CardTitle>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        <span>Last check: {lastCheck}</span>
                    </div>
                </div>
                
                <div className="flex gap-2 w-full sm:w-auto flex-shrink-0">
                     <Button variant="outline" size="sm" asChild className="h-9 px-3 flex-1 sm:flex-none">
                        <a href={checksUrl} className="flex items-center justify-center gap-2">
                            <Activity className="w-4 h-4" />
                            <span>Checks</span>
                        </a>
                    </Button>
                    <Button variant="default" size="sm" asChild className="h-9 px-3 bg-slate-900 hover:bg-black flex-1 sm:flex-none">
                        <a href={editUrl} className="flex items-center justify-center gap-2">
                            <span>Edit</span>
                            <ChevronRight className="w-4 h-4" />
                        </a>
                    </Button>
                </div>
                        </div>
                        <CardContent className="pt-2 pb-6">
                            <div className="flex flex-wrap gap-2 pt-1">
                    {monitor.lucene_q && (
                        <Badge variant="secondary" className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 transition-colors font-medium">
                            <Search className="w-3 h-3 mr-1.5 opacity-70" />
                            <span className="max-w-[200px] truncate">{monitor.lucene_q}</span>
                        </Badge>
                    )}

                    {monitor.prompt && (
                        <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-100 hover:bg-indigo-100 transition-colors font-medium">
                            <Sparkles className="w-3 h-3 mr-1.5 opacity-70" />
                            <span className="max-w-[150px] truncate">{monitor.prompt}</span>
                        </Badge>
                    )}

                    {monitor.aiModel?.title && (
                        <Badge variant="outline" className="border-indigo-200 bg-white text-indigo-600 font-semibold">
                            {monitor.aiModel.title}
                        </Badge>
                    )}

                    {monitor.process_images && (
                        <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">
                            <Eye className="w-3 h-3 mr-1.5" />
                            Vision
                        </Badge>
                    )}

                    {monitor.heartbeat_m && (
                        <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">
                            <Heart className="w-3 h-3 mr-1.5 fill-rose-100" />
                            {monitor.heartbeat_m}m
                        </Badge>
                    )}

                    {monitor.ds_email && (
                        <div className="flex items-center text-slate-400 px-1" title={`Email: ${monitor.ds_email}`}>
                            <Mail className="w-4 h-4" />
                        </div>
                    )}

                    {monitor.ds_ntfy_url && (
                        <div className="flex items-center text-slate-400 px-1" title="ntfy.sh enabled">
                            <Bell className="w-4 h-4" />
                        </div>
                    )}

                    {monitor.ds_webhook_url && (
                        <div className="flex items-center text-slate-400 px-1" title="Webhook enabled">
                            <Webhook className="w-4 h-4" />
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
